import { useEffect, useCallback }          from 'react'
import { supabase }                        from '@/lib/supabase'
import { usePlatformStore }                from '@/stores/platformStore'
import { useAppStore }                     from '@/tools/exmarked/stores/appStore'
import {
  getAllLocalSources,
  saveLocalSource,
  deleteLocalSource,
  touchLocalSource,
  supportsLocalFS,
  type LocalSourceRecord,
} from '@/tools/exmarked/lib/localDb'
import type { ConnectedRepo, LocalSource, ConnectedSource } from '@/tools/exmarked/types'
import { isLocalSource } from '@/tools/exmarked/types'

// ─── Merge + sort helper ──────────────────────────────────────────────────────

function sortSources(sources: ConnectedSource[]): ConnectedSource[] {
  return [...sources].sort((a, b) => {
    const aTime = a.last_accessed_at ?? a.added_at
    const bTime = b.last_accessed_at ?? b.added_at
    return bTime.localeCompare(aTime)
  })
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useRepos() {
  const { repos, setRepos } = useAppStore()
  const session = usePlatformStore((s) => s.session)

  // ── Load all sources (GitHub from Supabase, local from IndexedDB) ──────────
  const fetchSources = useCallback(async () => {
    const sources: ConnectedSource[] = []

    // GitHub repos
    if (session?.user) {
      const { data, error } = await supabase
        .from('connected_repos')
        .select('*')

      if (!error && data) {
        sources.push(...(data as ConnectedRepo[]))
      } else if (error) {
        console.error('[ExMarked] Failed to fetch GitHub repos:', error.message)
      }
    }

    // Local folders from IndexedDB
    try {
      const localRecords = await getAllLocalSources()
      const localSources: LocalSource[] = localRecords.map((r) => ({
        id:               r.id,
        source_type:      'local' as const,
        display_name:     r.display_name,
        path:             r.path,
        added_at:         r.added_at,
        last_accessed_at: r.last_accessed_at,
      }))
      sources.push(...localSources)
    } catch (err) {
      console.warn('[ExMarked] IndexedDB unavailable:', err)
    }

    setRepos(sortSources(sources))
  }, [session?.user, setRepos])

  useEffect(() => { void fetchSources() }, [fetchSources])

  // ── Add GitHub repo ────────────────────────────────────────────────────────
  const addRepo = async (
    owner: string,
    name: string,
    opts?: {
      description?:    string
      default_branch?: string
      is_private?:     boolean
      display_name?:   string
      avatar_url?:     string
    }
  ): Promise<ConnectedRepo> => {
    if (!session?.user) throw new Error('Not authenticated')

    const { data, error } = await supabase
      .from('connected_repos')
      .insert({
        user_id:        session.user.id,
        owner:          owner.trim(),
        name:           name.trim(),
        description:    opts?.description    ?? null,
        default_branch: opts?.default_branch ?? 'main',
        is_private:     opts?.is_private     ?? false,
        display_name:   opts?.display_name   ?? null,
        avatar_url:     opts?.avatar_url     ?? null,
      })
      .select()
      .single()

    if (error) {
      if (error.code === '23505') throw new Error('This repository is already connected.')
      throw error
    }

    await fetchSources()
    return data as ConnectedRepo
  }

  // ── Add local folder ───────────────────────────────────────────────────────
  const addLocalFolder = async (): Promise<LocalSource | null> => {
    if (!supportsLocalFS()) {
      throw new Error(
        'Your browser does not support the File System Access API. ' +
        'Please use Chrome or Edge to add local folders.',
      )
    }

    let handle: FileSystemDirectoryHandle
    try {
      handle = await window.showDirectoryPicker({ mode: 'read' })
    } catch (err) {
      // User cancelled the picker — not an error
      if (err instanceof Error && err.name === 'AbortError') return null
      throw err
    }

    const id: string     = crypto.randomUUID()
    const added_at       = new Date().toISOString()
    const record: LocalSourceRecord = {
      id,
      display_name:     handle.name,
      path:             handle.name,
      handle,
      added_at,
      last_accessed_at: null,
    }

    await saveLocalSource(record)
    await fetchSources()

    return {
      id,
      source_type:      'local',
      display_name:     handle.name,
      path:             handle.name,
      added_at,
      last_accessed_at: null,
    }
  }

  // ── Remove any source ──────────────────────────────────────────────────────
  const removeSource = async (source: ConnectedSource): Promise<void> => {
    if (isLocalSource(source)) {
      await deleteLocalSource(source.id)
    } else {
      const { error } = await supabase
        .from('connected_repos')
        .delete()
        .eq('id', source.id)
      if (error) throw error
    }
    setRepos(repos.filter((r) => r.id !== source.id))
  }

  // ── Update last-accessed timestamp ─────────────────────────────────────────
  const updateLastAccessed = async (source: ConnectedSource): Promise<void> => {
    if (isLocalSource(source)) {
      await touchLocalSource(source.id)
    } else {
      await supabase
        .from('connected_repos')
        .update({ last_accessed_at: new Date().toISOString() })
        .eq('id', source.id)
    }
  }

  return {
    repos,
    fetchSources,
    addRepo,
    addLocalFolder,
    removeSource,
    updateLastAccessed,
    supportsLocalFS: supportsLocalFS(),
  }
}
